import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import Modal from '../modal/Modal';
import Input from '../form-item/Input';
import FileUploader from '../file-uploader/FileUploader';
import UploadProgressBar from '../upload-progress/UploadProgressBar';
import { useUploadFailureDialog } from '../upload-progress/useUploadFailureDialog';
import MediaItemTable, { MediaTableRow } from '../media-item-table/MediaItemTable';

import TagSelector from '../tag-selector/TagSelector';
import useS3Upload, { UploadingFile, ValidationError } from '../../hooks/useS3Upload';
import { useCreateMediaItemMutation } from '../../app/services/mediaItems';
import {
  useCreateShotMutation,
  useUpdateShotMutation,
  useGetShotsQuery,
  shotsApi,
  Shot,
} from '../../app/services/shots';
import { useGetProjectQuery } from '../../app/services/projects';
import { isMaSequenceFileName, variationNameForUpload } from '../../utils/maSequenceFile';
import { getTableStatusKey, shotHasMedia } from '../../constant/shotTableStatus';
import { normalizeNfc } from '../../utils/text';

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ErrorMessage = styled.div.attrs({ className: 'bodytext-11' })`
  color: var(--soremo-green);
  text-align: center;
  padding: 8px;
`;

// 既存の「準備中」ショットと名前が一致したときのインラインヒント
const MatchHint = styled.div.attrs({ className: 'bodytext-11' })`
  color: var(--soremo-blue);
  display: flex;
  align-items: center;
  gap: 4px;
`;

// ショット名照合用にまとめて取得する件数の上限（TaskBoard の照合と同値）
const SHOT_LOOKUP_LIMIT = 200;

const MEDIA_TABLE_HELP = `各ファイルは独立したバリエーションとしてクライアントへ提示されます。
名前はクリックで編集できます。「名前_a」「名前_b」のように末尾にアルファベットを付けると、オプション（候補）のグループとしてまとめて比較表示されます。
並び替えは行頭のハンドル、またはヘッダー（名前・ファイル名）のクリックで行えます。
CSV（media, start, end, offset, track, gain 列）や Reaper プロジェクト（RPP）をアップロードすると MA シーケンスとして扱われ、紐づいたメディアは動画単位のミックス再生で確認できます。
MA シーケンスファイル名に「_v1.1.0」のようなバージョンが含まれる場合、バージョンより前が同じファイルは同一テイク履歴として積まれます。`;

// ===== Types =====

/** 提出前のローカルファイル（表でリネーム・並び替え可能） */
interface PendingMediaItem {
  id: string;
  file: File;
  /** バリエーション名（初期値 = 拡張子除去ファイル名。MA シーケンスはバージョン手前まで） */
  name: string;
}

let pendingSeq = 0;
const genPendingId = () => `pending-${++pendingSeq}`;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export interface SubmitModalProps {
  projectId: string;
  onClose: () => void;
  /** 提出完了時に提出先ショット ID を渡す（Preview への遷移用） */
  onSubmitted: (_shotId: string) => void;
}

const SubmitModal: React.FC<SubmitModalProps> = ({
  projectId,
  onClose,
  onSubmitted,
}) => {
  const [shotName, setShotName] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [pendingItems, setPendingItems] = useState<PendingMediaItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const { data: projectData } = useGetProjectQuery(
    { project_id: projectId },
    { skip: !projectId }
  );
  const shotTags = projectData?.data?.project?.shot_tags || [];

  // ===== 既存「準備中」ショットとの名前照合 =====
  // タスクリスト・ショットリスト経由で先に作られたショット（テイク未納品）と
  // 同名で提出した場合は、新規作成せずそのショットへメディアを追加する。
  // include_empty: true が必須（準備中 = メディア未登録は既定一覧から除外されるため）
  const { data: lookupShotsData } = useGetShotsQuery(
    {
      project_id: projectId,
      filter: 'all',
      include_empty: true,
      limit: SHOT_LOOKUP_LIMIT,
    },
    { skip: !projectId },
  );

  // 準備中ショットの名前 → Shot の Map（NFC 正規化キー・同名は先勝ち）
  const preparingShotByName = useMemo(() => {
    const map = new Map<string, Shot>();
    for (const shot of lookupShotsData?.data?.shots || []) {
      if (getTableStatusKey(shot.status, shotHasMedia(shot)) !== 'preparing') continue;
      const name = normalizeNfc((shot.shot_name || '').trim());
      if (name && !map.has(name)) map.set(name, shot);
    }
    return map;
  }, [lookupShotsData]);

  // 入力中のショット名と一致する準備中ショット（一致時はそのショットへ追加提出する）
  const matchedPreparingShot = useMemo(() => {
    const key = normalizeNfc(shotName.trim());
    return key ? preparingShotByName.get(key) ?? null : null;
  }, [preparingShotByName, shotName]);

  // ショット作成後の shotId（useS3Upload に反映するため state で管理）
  const [createdShotId, setCreatedShotId] = useState<string | undefined>();
  const pendingItemsRef = useRef<PendingMediaItem[]>([]);
  const uploadTriggeredRef = useRef(false);

  const dispatch = useDispatch();
  const [createShot] = useCreateShotMutation();
  const [updateShot] = useUpdateShotMutation();
  const [createMediaItem] = useCreateMediaItemMutation();

  // shotId が確定してから useS3Upload に渡す（initiate API が shot_id を必須とするため）
  const {
    uploadFiles,
    uploadingFiles,
    isUploading,
    abortFile,
    clearCompleted,
    retryFile,
    retryFiles,
    postProcessLabel,
    beginPostProcess,
    endPostProcess,
  } = useS3Upload({
    purpose: 'media_item',
    projectId,
    shotId: createdShotId,
    skipRegister: true,
    onValidationError: (errors: ValidationError[]) => {
      errors.forEach(e => console.warn(`バリデーションエラー: ${e.file.name} - ${e.message}`));
    },
  });

  // アップロード部分失敗時の「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログ
  const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({
    retryFiles,
  });

  // uploadFiles の最新参照を ref で保持（useEffect の依存配列から外すため）
  const uploadFilesRef = useRef(uploadFiles);
  uploadFilesRef.current = uploadFiles;

  const resolveUploadFailuresRef = useRef(resolveUploadFailures);
  resolveUploadFailuresRef.current = resolveUploadFailures;

  const clearCompletedRef = useRef(clearCompleted);
  clearCompletedRef.current = clearCompleted;

  const createMediaItemRef = useRef(createMediaItem);
  createMediaItemRef.current = createMediaItem;

  const postProcessControlsRef = useRef({
    begin: beginPostProcess,
    end: endPostProcess,
  });
  postProcessControlsRef.current = {
    begin: beginPostProcess,
    end: endPostProcess,
  };

  const onSubmittedRef = useRef(onSubmitted);
  onSubmittedRef.current = onSubmitted;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Phase 2: shotId 確定 → useS3Upload が新しい shotId で再構成 → アップロード開始
  useEffect(() => {
    if (!createdShotId || pendingItemsRef.current.length === 0 || uploadTriggeredRef.current) return;

    uploadTriggeredRef.current = true;
    const items = [...pendingItemsRef.current];
    const shotId = createdShotId;
    pendingItemsRef.current = [];

    (async () => {
      try {
        const uploadResults = await uploadFilesRef.current(items.map(item => item.file));

        // 失敗があれば「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログで解決
        const hadFailures = uploadResults.some((f: UploadingFile) => f.status === 'error');
        const resolved = await resolveUploadFailuresRef.current(uploadResults);
        if (resolved === null) {
          // キャンセル: 提出を中断し、再提出できる状態に戻す
          //（入力済みのショット名・ファイル一覧は保持される）
          clearCompletedRef.current();
          uploadTriggeredRef.current = false;
          setCreatedShotId(undefined);
          setIsSubmitting(false);
          return;
        }
        if (hadFailures) clearCompletedRef.current();

        // File 参照でアップロード結果（fileKey）を対応付ける
        const fileKeyByFile = new Map<File, string>();
        resolved.forEach((f: UploadingFile) => {
          if (f.fileKey) {
            fileKeyByFile.set(f.file, f.fileKey);
          }
        });

        if (fileKeyByFile.size === 0) {
          throw new Error('[S3アップロード] アップロード完了ファイルが0件です');
        }

        // 表の並び順どおりに逐次登録する（BE が作成順に variation_order を採番）。
        // 「失敗分を除いて続行」を選んだ場合、失敗ファイルはスキップする。
        // S3 転送が終わっても登録が残っているあいだは「処理中」として扱う
        //（進捗モーダルを閉じさせず、タブを閉じようとしたら引き止める）
        postProcessControlsRef.current.begin('メディアを登録中');
        try {
          for (const item of items) {
            const fileKey = fileKeyByFile.get(item.file);
            if (!fileKey) continue;
            try {
              await createMediaItemRef.current({
                shot_id: shotId,
                file_key: fileKey,
                variation_name: item.name.trim() || variationNameForUpload(item.file.name),
              }).unwrap();
            } catch (err: any) {
              const msg = err?.data?.message || err?.status || JSON.stringify(err);
              throw new Error(`[メディアアイテム作成] ${item.file.name}: ${msg}`);
            }
          }
        } finally {
          postProcessControlsRef.current.end();
        }

        // ショット一覧のキャッシュを無効化して再取得させる
        dispatch(shotsApi.util.invalidateTags(["Shots"]));

        onSubmittedRef.current(shotId);
        onCloseRef.current();
      } catch (err: any) {
        console.error('提出エラー (Phase 2):', err);
        setErrorMsg(err instanceof Error ? err.message : String(err));
      } finally {
        setIsSubmitting(false);
      }
    })();
  }, [createdShotId, dispatch]);

  // Phase 1: ショットを解決（同名の準備中ショットがあればそれ、なければ新規作成）
  // → shotId を state にセット → 再レンダリングで useS3Upload に shotId が反映
  const handleSubmit = useCallback(async () => {
    if (!shotName.trim() || pendingItems.length === 0) return;

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      let targetShotId: string | undefined;

      if (matchedPreparingShot) {
        // 既存の準備中ショットへ追加提出（新規作成しない）。
        // タグを選択した場合は既存タグへマージして反映する。
        targetShotId = matchedPreparingShot.id;
        if (selectedTagIds.length > 0) {
          const mergedTagIds = Array.from(
            new Set([...(matchedPreparingShot.tag_ids || []), ...selectedTagIds]),
          );
          await updateShot({
            project_id: projectId,
            shot_id: matchedPreparingShot.id,
            tag_ids: mergedTagIds,
          }).unwrap();
        }
      } else {
        const shotResult = await createShot({
          project_id: projectId,
          shot_name: shotName.trim(),
          status: 1,
          ...(selectedTagIds.length > 0 && { tag_ids: selectedTagIds }),
        }).unwrap();

        const shotData = shotResult?.data;
        targetShotId = shotData?.shot?.id ?? (shotData as any)?.id;

        if (!targetShotId) {
          throw new Error(`ショットIDを取得できません: ${JSON.stringify(shotResult).slice(0, 200)}`);
        }
      }

      // アイテムを ref に保存し、shotId を state にセット → useEffect が発火
      pendingItemsRef.current = pendingItems;
      setCreatedShotId(targetShotId);
    } catch (err: any) {
      console.error('提出エラー (Phase 1):', err);
      let message: string;
      if (err instanceof Error) {
        message = err.message;
      } else if (err?.data?.message) {
        message = `[ショット作成] ${err.data.message}`;
      } else if (err?.status) {
        message = `[ショット作成] API ${err.status}`;
      } else {
        message = String(err);
      }
      setErrorMsg(message);
      setIsSubmitting(false);
    }
  }, [projectId, shotName, pendingItems, selectedTagIds, createShot, updateShot, matchedPreparingShot]);

  // ===== 提出前テーブル =====

  // FileUploader は files={[]} でドロップ受付専用にしているため、
  // onChange には毎回「今回追加されたファイルのみ」が渡ってくる。
  const handleFilesAdded = useCallback((files: File[]) => {
    if (files.length === 0) return;
    setPendingItems(prev => {
      // 同名だと登録後に同一バリエーションのテイク（履歴）として併合されるため、
      // 追加時点で名前をユニーク化する（「名前 (2)」形式）。
      // MA シーケンスファイル（CSV / RPP）は同名 = テイク履歴が意図した挙動のため
      // ユニーク化しない（バージョン違いをまとめて提出できる）。
      const usedNames = new Set(prev.map(item => item.name));
      const added = files.map(file => {
        const base = variationNameForUpload(file.name);
        let name = base;
        if (!isMaSequenceFileName(file.name)) {
          for (let i = 2; usedNames.has(name); i++) {
            name = `${base} (${i})`;
          }
        }
        usedNames.add(name);
        return { id: genPendingId(), file, name };
      });
      return [...prev, ...added];
    });
  }, []);

  const handleRename = useCallback((id: string, newName: string) => {
    setPendingItems(prev =>
      prev.map(item => (item.id === id ? { ...item, name: newName } : item)),
    );
  }, []);

  const handleReorder = useCallback((orderedIds: string[]) => {
    setPendingItems(prev => {
      const byId = new Map(prev.map(item => [item.id, item]));
      const next = orderedIds
        .map(id => byId.get(id))
        .filter((item): item is PendingMediaItem => !!item);
      return next.length === prev.length ? next : prev;
    });
  }, []);

  const handleRemove = useCallback((id: string) => {
    setPendingItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const tableRows = useMemo<MediaTableRow[]>(
    () =>
      pendingItems.map(item => ({
        id: item.id,
        kind: 'variation' as const,
        name: item.name,
        fileName: item.file.name,
        sizeLabel: formatFileSize(item.file.size),
      })),
    [pendingItems],
  );

  const isSubmitDisabled =
    isSubmitting || isUploading || !shotName.trim() || pendingItems.length === 0;

  return (
    <Modal
      onClose={onClose}
      title="クライアントへ提出"
      description="メール通知で確認を依頼"
      size="wide"
      primaryAction={{
        label: "提出",
        onClick: handleSubmit,
        disabled: isSubmitDisabled,
        rightIcon: "chevron_right",
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
      }}
    >
      <Section>
        <Input
          label="ショット名"
          option="required"
          value={shotName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShotName(e.target.value)}
          placeholder="ショット名を入力"
        />
        {matchedPreparingShot && (
          <MatchHint>
            <span className="material-symbols-rounded" style={{ fontSize: 14 }}>info</span>
            既存の準備中ショット「{matchedPreparingShot.shot_name}」にメディアを追加します
          </MatchHint>
        )}
      </Section>

      {shotTags.length > 0 && (
        <TagSelector
          tags={shotTags}
          selectedIds={selectedTagIds}
          onChange={setSelectedTagIds}
        />
      )}

      <Section>
        <FileUploader
          files={[]}
          onChange={handleFilesAdded}
          multiple
          accept={{
            'video/*': [],
            'audio/*': [],
            'image/*': [],
            'application/pdf': [],
            'text/csv': ['.csv'],
            'application/octet-stream': ['.rpp'],
          }}
          height={120}
          placeholder="メディアをアップロード（MAシーケンス CSV / RPP も可）"
          disabled={isSubmitting || isUploading}
        />
        {(uploadingFiles.length > 0 || postProcessLabel) && (
          <UploadProgressBar
            files={uploadingFiles}
            onCancel={abortFile}
            onRetry={(file) => void retryFile(file.id)}
            compact
            postProcessLabel={postProcessLabel}
          />
        )}
      </Section>

      {/* 提出前のファイル一覧（リネーム・並び替え可能） */}
      {pendingItems.length > 0 && (
        <Section>
          <MediaItemTable
            rows={tableRows}
            onRename={handleRename}
            onReorder={handleReorder}
            onDelete={handleRemove}
            helpText={MEDIA_TABLE_HELP}
          />
        </Section>
      )}

      {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}

      {/* アップロード部分失敗ダイアログ */}
      {uploadFailureDialog}
    </Modal>
  );
};

export default SubmitModal;
